/**
 * Hook para gerenciar atalhos personalizados do usuário
 *
 * Permite que usuários personalizem seus próprios atalhos de teclado.
 * As personalizações sobrescrevem os defaults configurados por administradores.
 *
 * @example
 * const { shortcuts, isLoading, error, loadShortcuts, saveShortcuts, updateShortcut } = useUserShortcuts();
 */

import { useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { sendToTerminal, logger } from "../lib/terminalLogger";
import { LogCategory } from "../config/logging";
import type { ShortcutsState } from "./useShortcutsLoader";
import type { KeyCombo } from "../contexts/KeyboardContext";

// ═══════════════════════════════════════════════════════════════════════════
// TIPOS
// ═══════════════════════════════════════════════════════════════════════════

export interface UseUserShortcutsReturn {
	/** Atalhos do usuário (merge defaults + personalizações) */
	shortcuts: ShortcutsState;
	/** Indica se está carregando */
	isLoading: boolean;
	/** Erro ocorrido (null se sucesso) */
	error: string | null;
	/** Indica se carregou com sucesso pelo menos uma vez */
	isLoaded: boolean;
	/** Função para carregar/recarregar atalhos */
	loadShortcuts: () => Promise<ShortcutsState | null>;
	/** Função para salvar todos os atalhos (replace completo) */
	saveShortcuts: (shortcuts: ShortcutsState) => Promise<boolean>;
	/** Função para atualizar um atalho específico (faz merge automático) */
	updateShortcut: (
		toolId: string,
		actionId: string,
		keys: KeyCombo,
	) => Promise<boolean>;
	/** Função para remover um atalho personalizado (volta para default) */
	removeShortcut: (toolId: string, actionId: string) => Promise<boolean>;
	/** Função para limpar erro */
	clearError: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export function useUserShortcuts(): UseUserShortcutsReturn {
	const [shortcuts, setShortcuts] = useState<ShortcutsState>({});
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isLoaded, setIsLoaded] = useState(false);

	// Evitar múltiplas chamadas simultâneas
	const loadingRef = useRef(false);

	/**
	 * Carrega atalhos do usuário (merge defaults + personalizações)
	 * GET /users/me/shortcuts
	 */
	const loadShortcuts =
		useCallback(async (): Promise<ShortcutsState | null> => {
			// Evitar chamadas duplicadas
			if (loadingRef.current) {
				logger.debug(
					"[USER_SHORTCUTS] Já está carregando, ignorando chamada duplicada",
				);
				return null;
			}

			loadingRef.current = true;
			setIsLoading(true);
			setError(null);

			try {
				sendToTerminal(
					"🔍 [USER_SHORTCUTS] Carregando atalhos do usuário...",
					{},
					LogCategory.API,
				);

				const response = await api("/users/me/shortcuts", {
					method: "GET",
				});

				if (
					response?.success &&
					response.data &&
					typeof response.data === "object"
				) {
					const userShortcuts = response.data as ShortcutsState;

					setShortcuts(userShortcuts);
					setIsLoaded(true);

					const bindingCount = Object.values(userShortcuts).reduce(
						(sum, actions) => sum + Object.keys(actions).length,
						0,
					);

					sendToTerminal(
						"✅ [USER_SHORTCUTS] Atalhos do usuário carregados",
						{
							bindings: bindingCount,
							tools: Object.keys(userShortcuts).length,
						},
						LogCategory.API,
					);

					logger.info(
						`[USER_SHORTCUTS] ${bindingCount} atalhos carregados com sucesso`,
					);

					return userShortcuts;
				} else {
					throw new Error("Resposta inválida da API de atalhos do usuário");
				}
			} catch (err: any) {
				const message = err?.message || "Erro ao carregar atalhos do usuário";

				setError(message);
				setShortcuts({}); // Sem fallback - fica vazio em caso de erro

				sendToTerminal(
					"❌ [USER_SHORTCUTS] Erro ao carregar atalhos do usuário",
					{
						error: message,
					},
					LogCategory.ERROR,
				);

				logger.error("[USER_SHORTCUTS] Falha ao carregar atalhos:", message);

				return null;
			} finally {
				setIsLoading(false);
				loadingRef.current = false;
			}
		}, []);

	/**
	 * Salva todos os atalhos do usuário (replace completo)
	 * PUT /users/me/shortcuts
	 *
	 * ⚠️ IMPORTANTE: Faz replace completo. Sempre envie TODOS os atalhos que deseja manter.
	 */
	const saveShortcuts = useCallback(
		async (newShortcuts: ShortcutsState): Promise<boolean> => {
			setIsLoading(true);
			setError(null);

			try {
				sendToTerminal(
					"💾 [USER_SHORTCUTS] Salvando atalhos do usuário...",
					{
						bindings: Object.values(newShortcuts).reduce(
							(sum, actions) => sum + Object.keys(actions).length,
							0,
						),
					},
					LogCategory.API,
				);

				const response = await api("/users/me/shortcuts", {
					method: "PUT",
					body: JSON.stringify({ shortcuts: newShortcuts }),
				});

				if (response?.success) {
					setShortcuts(newShortcuts);

					sendToTerminal(
						"✅ [USER_SHORTCUTS] Atalhos salvos com sucesso",
						{},
						LogCategory.API,
					);
					logger.info("[USER_SHORTCUTS] Atalhos salvos com sucesso");

					return true;
				} else {
					throw new Error(response?.error || "Erro ao salvar atalhos");
				}
			} catch (err: any) {
				const message = err?.message || "Erro ao salvar atalhos do usuário";

				setError(message);

				sendToTerminal(
					"❌ [USER_SHORTCUTS] Erro ao salvar atalhos",
					{
						error: message,
					},
					LogCategory.ERROR,
				);

				logger.error("[USER_SHORTCUTS] Falha ao salvar atalhos:", message);

				return false;
			} finally {
				setIsLoading(false);
			}
		},
		[],
	);

	/**
	 * Atualiza um atalho específico (faz merge automático com atalhos existentes)
	 * Busca os atalhos atuais, atualiza apenas o especificado, e salva tudo
	 */
	const updateShortcut = useCallback(
		async (
			toolId: string,
			actionId: string,
			keys: KeyCombo,
		): Promise<boolean> => {
			// 1. Buscar atalhos atuais
			const current = await loadShortcuts();
			if (current === null) {
				// Se falhou ao carregar, tentar usar estado local
				const updated = {
					...shortcuts,
					[toolId]: {
						...shortcuts[toolId],
						[actionId]: keys,
					},
				};
				return await saveShortcuts(updated);
			}

			// 2. Atualizar apenas o atalho específico
			const updated = {
				...current,
				[toolId]: {
					...current[toolId],
					[actionId]: keys,
				},
			};

			// 3. Salvar tudo
			return await saveShortcuts(updated);
		},
		[shortcuts, loadShortcuts, saveShortcuts],
	);

	/**
	 * Remove um atalho personalizado (volta para usar o default)
	 * Remove a personalização, deixando o default aparecer no próximo GET
	 */
	const removeShortcut = useCallback(
		async (toolId: string, actionId: string): Promise<boolean> => {
			// 1. Buscar atalhos atuais
			const current = await loadShortcuts();
			if (current === null) {
				// Se falhou ao carregar, tentar usar estado local
				const updated = { ...shortcuts };
				if (updated[toolId] && updated[toolId][actionId]) {
					delete updated[toolId][actionId];

					// Se a tool não tem mais personalizações, remover completamente
					if (Object.keys(updated[toolId]).length === 0) {
						delete updated[toolId];
					}
				}
				return await saveShortcuts(updated);
			}

			// 2. Remover a personalização
			const updated = { ...current };
			if (updated[toolId] && updated[toolId][actionId]) {
				delete updated[toolId][actionId];

				// Se a tool não tem mais personalizações, remover completamente
				if (Object.keys(updated[toolId]).length === 0) {
					delete updated[toolId];
				}
			}

			// 3. Salvar
			return await saveShortcuts(updated);
		},
		[shortcuts, loadShortcuts, saveShortcuts],
	);

	const clearError = useCallback(() => {
		setError(null);
	}, []);

	return {
		shortcuts,
		isLoading,
		error,
		isLoaded,
		loadShortcuts,
		saveShortcuts,
		updateShortcut,
		removeShortcut,
		clearError,
	};
}
